import { useEffect, useRef, useState } from "react"
import { Image, Form, message, Upload, Flex, Button } from "antd"
import { QuestionCircleOutlined, LoadingOutlined, PlusOutlined, CameraOutlined, DeleteOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { uploadFile, downloadFile, deleteFile } from "../../../services/WebServices"
import { uploadPublicFormAttachment, downloadPublicFormAttachment, deletePublicFormAttachment } from "../../../services/publicFormServices"

const ImageInput = (props) => {
    const { label, tooltip, helpText, mandatory, readOnly, id, textAlignment="left", useCameraMode=false, formValues, shareToken, tenantId } = props
    const { t } = useTranslation();
    const params = useParams();
    const rules = mandatory ? [{ required: true }] : undefined;
    const help = tooltip ? { title: helpText, icon: <QuestionCircleOutlined /> }
        : undefined;
    const [imageUrl, setImageUrl] = useState("")
    const [loading, setLoading] = useState(false)
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [cameraModalOpen, setCameraModalOpen] = useState(false);
    const [stream, setStream] = useState(null);
    const [imageFileName, setImageFileName] = useState("");
    const [pendingFileName, setPendingFileName] = useState("");
    const lastFetchedRef = useRef("");

    const isPublicForm = !!shareToken;

    const normalizeRequestId = (value) => {
        if (value === undefined || value === null) return null;
        const str = String(value).trim();
        if (!str) return null;
        if (str === "new" || str === "undefined" || str === "null") return null;
        return str;
    };

    const requestId = normalizeRequestId(params.requestId || props.requestId);

    useEffect(() => {
        const fieldValue = formValues?.[id];
        if (!fieldValue) {
            setImageUrl("");
            setImageFileName("");
            return;
        }

        if (typeof fieldValue === "string") {
            const safeFieldValue = normalizeFileName(fieldValue);
            if (pendingFileName && safeFieldValue === pendingFileName && !imageUrl) {
                return;
            }
            if (fieldValue.startsWith("data:")) {
                setImageUrl(fieldValue);
                setImageFileName("");
            } else {
                setImageFileName(safeFieldValue);
                fetchImageFromBucket(safeFieldValue);
            }
            return;
        }

        if (typeof fieldValue === "object") {
            const nameFromValue = fieldValue?.name || fieldValue?.fileName || fieldValue?.filename;
            if (nameFromValue) {
                const safeName = normalizeFileName(nameFromValue);
                if (pendingFileName && safeName === pendingFileName && !imageUrl) {
                    return;
                }
                setImageFileName(safeName);
                fetchImageFromBucket(safeName);
            } else if (fieldValue?.url) {
                setImageUrl(fieldValue.url);
            }
        }
    }, [formValues, id, requestId, shareToken, tenantId, pendingFileName]);

    useEffect(() => {
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [stream]);

    const handleRemove = async () => {
        try {
            if (requestId && imageFileName) {
                setLoading(true);
                if (isPublicForm) {
                    await deletePublicFormAttachment(requestId, imageFileName, shareToken, tenantId);
                } else {
                    await deleteFile(requestId, imageFileName);
                }
            }
        } catch (error) {
            message.error(t("Failed to delete image"));
        } finally {
            setLoading(false);
            setImageUrl("");
            setImageFileName("");
            if (props.form) {
                props.form.setFieldValue(id, "");
            }
        }
    };

    const beforeUpload = (file) => {
        if (!requestId) {
            message.error(t("Your request must be saved before you can upload images."));
            return Upload.LIST_IGNORE;
        }
        setPendingFileName(normalizeFileName(file.name));
        const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
        if (!isJpgOrPng) {
            message.error(t("You can only upload JPG/PNG file"));
        }
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
            message.error(t("Image must be smaller than 2MB"));
        }
        return isJpgOrPng && isLt2M;
    };

    const getBase64 = (img, callback) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.addEventListener("load", () => {
                if (callback) callback(reader.result);
                resolve(reader.result);
            });
            reader.addEventListener("error", () => reject(new Error("Failed to read file")));
            reader.readAsDataURL(img);
        });
    };

    const inferContentTypeFromName = (fileName) => {
        const extension = (fileName || "").split(".").pop().toLowerCase();
        const mimeTypes = {
            jpg: "image/jpeg",
            jpeg: "image/jpeg",
            png: "image/png",
            gif: "image/gif",
            bmp: "image/bmp",
            tiff: "image/tiff",
            tif: "image/tiff",
            webp: "image/webp",
            svg: "image/svg+xml",
        };
        return mimeTypes[extension] || "application/octet-stream";
    };

    const extractContentFromResponse = (response) => {
        return (
            response?.result?.body?.content ||
            response?.result?.body?.result?.content ||
            response?.body?.content ||
            response?.content ||
            null
        );
    };

    const extractContentTypeFromResponse = (response, fileName) => {
        const headers = response?.result?.headers || response?.headers || {};
        if (headers["Content-Type"]) {
            if (Array.isArray(headers["Content-Type"])) {
                return headers["Content-Type"][0];
            }
            if (typeof headers["Content-Type"] === "string") {
                return headers["Content-Type"];
            }
        }
        return inferContentTypeFromName(fileName);
    };

    const normalizeFileName = (value) => {
        if (!value) return "";
        const asString = String(value);
        const normalized = asString.replace(/\\/g, "/");
        const parts = normalized.split("/");
        return parts[parts.length - 1] || "";
    };

    const fetchImageFromBucket = async (fileName) => {
        const safeFileName = normalizeFileName(fileName);
        if (!requestId || !safeFileName) return;
        if (lastFetchedRef.current === safeFileName && imageUrl) return;
        try {
            setLoading(true);
            lastFetchedRef.current = safeFileName;
            let response;
            if (isPublicForm) {
                response = await downloadPublicFormAttachment(requestId, safeFileName, shareToken, tenantId);
            } else {
                response = await downloadFile(requestId, safeFileName);
            }

            const content = extractContentFromResponse(response);
            if (!response?.success || !content) {
                throw new Error(response?.message || "Failed to download image");
            }

            const contentType = extractContentTypeFromResponse(response, safeFileName);
            const dataUrl = `data:${contentType};base64,${content}`;
            setImageUrl(dataUrl);
        } catch (error) {
            message.error(t("Failed to load image"));
        } finally {
            setLoading(false);
        }
    };

    const uploadImageFile = async (file, onSuccess, onError) => {
        if (!requestId) {
            const err = new Error("Request ID is required for image upload");
            message.error(t("Your request must be saved before you can upload images."));
            if (onError) onError(err);
            return;
        }

        try {
            setLoading(true);

            let response;
            if (isPublicForm) {
                response = await uploadPublicFormAttachment(requestId, shareToken, file, tenantId);
                response = { success: response.status === 200, data: response.data, message: response.message };
            } else {
                response = await uploadFile(file, requestId);
            }

            if (!response?.success) {
                throw new Error(response?.message || "Upload failed");
            }

            const uploadedNameRaw = response?.data?.fileName || response?.data?.name || file.name;
            const uploadedName = normalizeFileName(uploadedNameRaw);
            setImageFileName(uploadedName);
            if (props.form) {
                props.form.setFieldValue(id, uploadedName);
            }

            getBase64(file, (url) => {
                setImageUrl(url);
            });

            await fetchImageFromBucket(uploadedName);

            message.success(t("Image uploaded successfully"));
            if (onSuccess) onSuccess(response.data, file);
        } catch (error) {
            message.error(error.message || t("Failed to upload image"));
            if (onError) onError(error);
        } finally {
            setLoading(false);
            setPendingFileName("");
        }
    };

    const customRequest = (options) => {
        const { file, onSuccess, onError } = options;
        uploadImageFile(file, onSuccess, onError);
    };

    // Camera functionality
    const openCamera = async () => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({ 
                video: { facingMode: 'environment' } // Use back camera if available
            });
            setStream(mediaStream);
            setCameraModalOpen(true); // Using this state to show/hide camera interface
        } catch (error) {
            message.error(t("Unable to access camera. Please check permissions."));
        }
    };

    const closeCameraModal = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        setCameraModalOpen(false);
    };

    const captureImage = () => {
        // Use the inline video element when camera is not in modal
        const video = document.getElementById('camera-video-inline') || document.getElementById('camera-video');
        if (!video) {
            message.error(t("Camera not available"));
            return;
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        
        canvas.toBlob((blob) => {
            const safeLabel = String(label || "captured-image")
                .trim()
                .replace(/[^a-zA-Z0-9-_]+/g, "-")
                .replace(/^-+|-+$/g, "") || "captured-image";
            const file = new File([blob], `${safeLabel}.jpg`, { type: 'image/jpeg' });
            uploadImageFile(file);
            closeCameraModal();
        }, 'image/jpeg', 0.8);
    };

    const uploadButton = (
        <button
            style={{
                border: 0,
                background: 'none',
            }}
            type="button"
        >
            {loading ? <LoadingOutlined /> : <PlusOutlined />}
            <div
                style={{
                    marginTop: 8,
                }}
            >
                {t('Upload')}
            </div>
        </button>
    );

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
    };

    return (
        <div className="form-element">
            <Form.Item
                name={id}
                label={label}
                style={{ textAlign: "center" }}
                tooltip={help}
                rules={rules}
            >
                <Flex align="center" gap={8} vertical>
                    {
                        readOnly && !imageUrl ? <span className="text-gray-400">{t('No image uploaded')}</span>
                        : 
                        <>
                            {/* Show upload when camera mode is OFF and camera modal is not open */}
                            {!useCameraMode && !cameraModalOpen && (
                                <Upload
                                    name="avatar"
                                    listType="picture-card"
                                    className={"avatar-uploader-" + textAlignment}
                                    showUploadList={false}
                                    onPreview={handlePreview}
                                    beforeUpload={beforeUpload}
                                    customRequest={customRequest}
                                    style={{width: '300px', border: '1px dashed #ccc'}}
                                >
                                    {imageUrl ? (
                                        <img
                                            src={imageUrl}
                                            alt="avatar"
                                            style={{
                                                width: '100%', height: "100%", objectFit: "contain",
                                                cursor: 'pointer'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent Upload component's click event
                                                setPreviewImage(imageUrl);
                                                setPreviewOpen(true);
                                            }}
                                        />
                                    ) : (
                                        uploadButton
                                    )}
                                </Upload>
                            )}
                            
                            {/* Show camera interface when camera mode is ON and camera modal is open */}
                            {useCameraMode && cameraModalOpen && (
                                <div style={{
                                    width: '300px',
                                    height: '300px',
                                    border: '1px dashed #ccc',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f5f5f5'
                                }}>
                                    {stream ? (
                                        <video
                                            id="camera-video-inline"
                                            autoPlay
                                            playsInline
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: '8px'
                                            }}
                                            ref={(video) => {
                                                if (video && stream) {
                                                    video.srcObject = stream;
                                                }
                                            }}
                                        />
                                    ) : (
                                        <div style={{ textAlign: 'center', color: '#666' }}>
                                            <CameraOutlined style={{ fontSize: '48px', marginBottom: '8px' }} />
                                            <div>{t("Camera Starting...")}</div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Show captured/uploaded image preview when camera mode is ON but camera is closed */}
                            {useCameraMode && !cameraModalOpen && imageUrl && (
                                <div style={{
                                    width: '300px',
                                    height: '300px',
                                    border: '1px dashed #ccc',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f5f5f5',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setPreviewImage(imageUrl);
                                    setPreviewOpen(true);
                                }}>
                                    <img
                                        src={imageUrl}
                                        alt="captured"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            borderRadius: '8px'
                                        }}
                                    />
                                </div>
                            )}
                            
                            <Flex gap={8}>
                                {/* If camera mode is ON */}
                                {useCameraMode && !readOnly && (
                                    !cameraModalOpen ? (
                                        <Button
                                            type="default"
                                            icon={<CameraOutlined />}
                                            onClick={openCamera}
                                            style={{
                                                fontWeight: "400",
                                                color: "rgba(22, 119, 255, 1)",
                                                borderColor: "rgba(22, 119, 255, 1)",
                                            }}
                                        >
                                            {t("Open Camera")}
                                        </Button>
                                    ) : (
                                        <>
                                            <Button
                                                type="primary"
                                                icon={<CameraOutlined />}
                                                onClick={captureImage}
                                                disabled={!stream}
                                            >
                                                {t("Capture")}
                                            </Button>
                                            <Button
                                                type="default"
                                                onClick={closeCameraModal}
                                            >
                                                {t("Cancel")}
                                            </Button>
                                        </>
                                    )
                                )}
                                {imageUrl && !readOnly && (
                                    <Button
                                        type="default"
                                        icon={<DeleteOutlined />}
                                        onClick={handleRemove}
                                        style={{
                                            fontWeight: "400",
                                            color: "rgba(255, 77, 79, 1)",
                                            borderColor: "rgba(255, 77, 79, 1)",
                                        }}
                                    >
                                        {t("Remove")}
                                    </Button>
                                )}
                            </Flex>
                        </>
                    }
                </Flex>
            </Form.Item>

            {/* Camera Modal - Now optional/fallback */}
            {/* Remove modal since we're showing camera inline */}

            {previewImage && (
                <Image
                    wrapperStyle={{
                        display: 'none',
                    }}
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: (visible) => setPreviewOpen(visible),
                        afterOpenChange: (visible) => !visible && setPreviewImage(''),
                    }}
                    src={previewImage}
                />
            )}
        </div>
    )
}

export default ImageInput
